<template>
    <ai-message-vetting-agent-details :agentId="props.agentId">
        <div class="col-span-12 lg:col-span-10">
            <page-callout section="form-submission" shadow class="mb-6" />

            <page-header class="mb-2 md:mb-2">Standards using this agent</page-header>

            <page-controls>
                <template v-slot:left>
                    <div class="grow @3xl/controls:max-w-md">
                        <vx-filterable :active="active" @toggle="toggleFilterable" data-test="filters">
                            <template v-slot:placeholder>
                                <span class="mr-2">Find</span>
                                <vx-badge
                                    v-if="filtersCount > 0"
                                    data-test="reset"
                                    :borders="false"
                                    size="xs"
                                    @click.stop="resetFilters"
                                >
                                    {{ pluralized('filter', filtersCount, true) }}
                                    <font-awesome-icon widthAuto :icon="faCircleXmark" />
                                </vx-badge>
                            </template>
                            <template v-slot:form>
                                <form @submit.prevent="applyFilters" class="flex flex-col gap-4">
                                    <div>
                                        <filter-label :canReset="!!filters.query" @reset="filters.query = ''">
                                            Keyword Search
                                        </filter-label>
                                        <vx-input
                                            type="text"
                                            data-test="query"
                                            placeholder="Search"
                                            maxlength="80"
                                            size="lg"
                                            v-model="filters.query"
                                        >
                                            <template v-slot:right>
                                                <font-awesome-icon
                                                    widthAuto
                                                    class="text-xs"
                                                    :icon="faMagnifyingGlass"
                                                />
                                            </template>
                                        </vx-input>
                                    </div>

                                    <div class="flex gap-2 pt-4">
                                        <vx-button
                                            data-test="cancel"
                                            type="button"
                                            color="muted-light"
                                            size="lg"
                                            @click="cancel"
                                        >
                                            Cancel
                                        </vx-button>
                                        <vx-button data-test="apply" type="submit" size="lg" block>
                                            Apply
                                            <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                                        </vx-button>
                                    </div>
                                </form>
                            </template>
                        </vx-filterable>
                    </div>
                    <div class="flex w-full gap-2 @xl/controls:w-auto">
                        <vx-button-group size="lg" class="w-full">
                            <vx-select
                                v-model="filters.sort_field"
                                @update:modelValue="applyFilters"
                                :options="[
                                    {
                                        value: 'name',
                                        label: 'Sort by: Name',
                                    },
                                    {
                                        value: 'id',
                                        label: 'Sort by: Created',
                                    },
                                ]"
                            ></vx-select>
                            <vx-button
                                @click.prevent="
                                    filters.sort_dir = filters.sort_dir === 'asc' ? 'desc' : 'asc';
                                    applyFilters();
                                "
                                type="button"
                                color="white"
                                shadow
                            >
                                <font-awesome-icon
                                    widthAuto
                                    :icon="filters.sort_dir === 'asc' ? faArrowUpWideShort : faArrowDownWideShort"
                                ></font-awesome-icon>
                            </vx-button>
                        </vx-button-group>
                    </div>
                </template>
            </page-controls>

            <page-loading
                v-if="(loading || loadingDefault) && !list.length && !defaultStandard"
                data-test="loading"
            ></page-loading>

            <template v-else-if="!list.length && !defaultStandard">
                <empty-state v-if="!isEqual(defaultFilters(), fetchParams)" @reset="resetFilters" />
                <empty-state v-else data-test="empty-state">
                    <div class="flex flex-col items-center gap-1">
                        <span class="font-bold text-2xl">Not assigned to any standards yet</span>
                        <span class="text-base">Visit a brand standard to assign this agent.</span>
                    </div>
                    <vx-button
                        :href="`/settings/teams/${teamId}#/message-standards`"
                        color="muted"
                        data-test="go-to-standards-button"
                    >
                        Go to Brand Standards
                        <font-awesome-icon widthAuto :icon="faListUl" />
                    </vx-button>
                </empty-state>
            </template>

            <template v-else>
                <vx-table class="xl:grid-cols-3-auto mb-6" rowBreakpoint="xl" data-test="standards-table">
                    <vx-table-row isHeader>
                        <div>Name</div>
                        <div>Targets</div>
                    </vx-table-row>

                    <message-standard-row
                        v-if="defaultStandard"
                        :modelValue="{ name: 'Defaults', team_id: teamId }"
                        :key="'default'"
                        isDefault
                        readonly
                    ></message-standard-row>

                    <message-standard-row
                        v-for="item in customStandardsList"
                        :modelValue="item"
                        :key="item.id"
                        readonly
                    ></message-standard-row>
                </vx-table>

                <div v-if="fetchParams.cursor" class="mt-8">
                    <vx-button
                        @click="loadData()"
                        color="muted-dark"
                        size="xl"
                        data-test="load-more-btn"
                        class="grow-0"
                        :loading="loading"
                    >
                        Load More
                        <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
                    </vx-button>
                </div>
            </template>
        </div>
    </ai-message-vetting-agent-details>
</template>

<script setup lang="ts">
import {
    faArrowDownWideShort,
    faArrowUpWideShort,
    faMagnifyingGlass,
    faSpinner,
    faListUl,
    faCircleXmark,
    faCircleCheck,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxButton,
    VxButtonGroup,
    VxInput,
    VxSelect,
    VxTable,
    VxTableRow,
    VxFilterable,
    VxBadge,
} from '@voxie/frontend-components';
import { computed, onMounted, ref } from 'vue';
import { isEqual } from 'lodash';
import PageControls from '~/components/general/PageControls.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import EmptyState from '~/components/general/EmptyState.vue';
import AiMessageVettingAgentDetails from './AiMessageVettingAgentDetails.vue';
import { messageStandardsClient } from '~/services';
import type { CursorPaginationParams } from '~/services/BaseClient.types';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { pluralized } from '~/components/filters';
import FilterLabel from '~/components/general/FilterLabel.vue';
import PageCallout from '~/components/general/PageCallout.vue';
import MessageStandardRow from '~/components/settings/teams/message-standards/list/MessageStandardRow.vue';
import { useMessageStandardsGroupsCount } from '~/components/settings/teams/message-standards/list/useMessageStandardsGroupsCount';
import type { MessageStandardType } from '~/services/messageStandards/messageStandardsClient.types';

const props = defineProps<{
    agentId: number;
}>();

const teamId = Spark.state.currentTeam.id;

const { list, loading, fetchParams, fetchRecords } = useCursorPagination<MessageStandardType, CursorPaginationParams>();
const { fetchMessageStandardsGroupsCount } = useMessageStandardsGroupsCount();

const defaultStandard = ref<MessageStandardType | null>(null);
const loadingDefault = ref(false);

const customStandardsList = computed<MessageStandardType[]>(() => list.value);

const defaultFilters = (): CursorPaginationParams => ({
    per_page: 10,
    cursor: null,
    query: '',
    sort_field: 'name',
    sort_dir: 'desc',
});

const checkAgentBelongsToDefault = (standard: MessageStandardType): boolean => {
    const marketingAgents = standard.marketing_rules?.ai_message_vetting_agents ?? [];
    const transactionalAgents = standard.transactional_rules?.ai_message_vetting_agents ?? [];
    return (
        marketingAgents.some((a) => a.id === props.agentId) || transactionalAgents.some((a) => a.id === props.agentId)
    );
};

const loadDefaultStandard = async () => {
    loadingDefault.value = true;
    try {
        const response = await messageStandardsClient.getDefaultMessageStandard({ teamId });

        if (checkAgentBelongsToDefault(response.data)) {
            defaultStandard.value = response.data;
        }
    } finally {
        loadingDefault.value = false;
    }
};

const active = ref(false);
const filters = ref(defaultFilters());
const filtersCount = computed(() => (fetchParams.value.query ? 1 : 0));

const toggleFilterable = (event: boolean) => {
    active.value = event;
};

const resetFilters = () => {
    filters.value = defaultFilters();
    fetchParams.value = defaultFilters();
    active.value = false;
    loadDefaultStandard();
    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...filters.value,
        cursor: null,
    };
    active.value = false;
    loadData();
};

const cancel = async () => {
    filters.value = { ...fetchParams.value };
    active.value = false;
};

onMounted(() => {
    resetFilters();
});

const loadData = () => {
    const request = messageStandardsClient.getMessageStandards(
        { teamId },
        {
            ...fetchParams.value,
            ai_message_vetting_agent_id: props.agentId,
        }
    );

    fetchRecords(request).then((data) => {
        if (data?.length) {
            const messageStandardIds = data.map((standard) => standard.id!);
            fetchMessageStandardsGroupsCount({ teamId }, messageStandardIds);
        }
    });
};
</script>
